import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { History, Search } from "lucide-react";

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit-log", { params: { limit: 200 } });
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = items.filter((i) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (i.action || "").toLowerCase().includes(f) ||
           (i.user_email || "").toLowerCase().includes(f);
  });

  return (
    <div className="p-8 space-y-6" data-testid="audit-log-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--hi-orange)" }}>WHO DID WHAT</div>
        <h1 className="font-heading font-black text-4xl mt-2 tracking-tight" style={{ color: "var(--hi-olive)" }}>
          Audit log
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Every consequential action — bulk syncs, imports, programme exports — is recorded here.
          Entries are retained for 365 days then automatically pruned.
        </p>
      </div>

      <Card className="rounded-none border-border p-4">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by action or user email…"
            className="rounded-none border-slate-300 h-10 pl-9"
            data-testid="audit-filter"
          />
        </div>
      </Card>

      <Card className="rounded-none border-border overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">When</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">User</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Action</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(6)].map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-6" /></TableCell></TableRow>)
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-16 text-muted-foreground">
                {items.length === 0 ? "No audit entries yet — run a bulk sync, import, or programme export to populate the log." : "No entries match the filter."}
              </TableCell></TableRow>
            ) : filtered.map((e, i) => (
              <TableRow key={e.id || i} className="border-border" data-testid={`audit-row-${i}`}>
                <TableCell className="font-mono text-xs">
                  {new Date(e.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </TableCell>
                <TableCell className="text-sm">{e.user_email || "—"}</TableCell>
                <TableCell>
                  <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                    style={{ backgroundColor: e.user_role === "admin" ? "var(--hi-olive)" : "#e6dfcc", color: e.user_role === "admin" ? "white" : "#1f2a14" }}>
                    {e.user_role || "—"}
                  </span>
                </TableCell>
                <TableCell><span className="text-xs font-mono font-bold">{e.action}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono max-w-md truncate">
                  {e.details ? JSON.stringify(e.details) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
